import { Combobox } from "@dashboard/components/Combobox";
import { Box, Button, Checkbox, Input, Option, Text } from "@saleor/macaw-ui-next";
import { useIntl } from "react-intl";

import { useCegidProductSearchQuery } from "./graphql";
import { invoiceLineCount } from "./mergeGroups";
import { cegidInvoiceDialogMessages as messages } from "./messages";
import { CegidInvoiceFormLine } from "./types";

interface CegidInvoiceLineRowProps {
  line: CegidInvoiceFormLine;
  onChange: (patch: Partial<CegidInvoiceFormLine>) => void;
  // Set when the row is a merged group; `line` is its first member.
  members?: CegidInvoiceFormLine[];
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
  onUnmerge?: () => void;
}

const formatPercentage = (value: number | null) => (value === null ? "?" : `${value}%`);

const CegidInvoiceLineRow = ({
  line,
  onChange,
  members,
  selected,
  onSelectedChange,
  onUnmerge,
}: CegidInvoiceLineRowProps) => {
  const intl = useIntl();
  const [searchProducts, searchResult] = useCegidProductSearchQuery();
  const searchProductsList = searchResult.data?.cegidProductSearch ?? [];

  const searchOptions: Option[] = searchProductsList.map(product => ({
    value: product.itemCode,
    label: `${product.itemCode} — ${product.itemDescription}`,
  }));

  const extraTextInput = (
    <Input
      name={`cegid-extra-text-${line.orderLineId ?? "shipping"}`}
      label={intl.formatMessage(messages.extraTextLabel)}
      helperText={intl.formatMessage(messages.extraTextHelper)}
      value={line.extraText}
      onChange={event => onChange({ extraText: event.target.value })}
    />
  );

  const codeValue: Option | null = line.itemCode
    ? { value: line.itemCode, label: line.itemCode }
    : null;

  return (
    <Box
      display="grid"
      __gridTemplateColumns="1.4fr 1fr 1.4fr"
      gap={3}
      alignItems="start"
      paddingY={3}
      borderBottomWidth={1}
      borderBottomStyle="solid"
      borderColor="default1"
    >
      <Box display="grid" gap={1}>
        <Box display="flex" gap={2} alignItems="center">
          {onSelectedChange && (
            <Checkbox
              checked={!!selected}
              onCheckedChange={checked => onSelectedChange(!!checked)}
              aria-label={intl.formatMessage(messages.selectLine)}
              data-test-id="cegid-select-line"
            />
          )}
          <Text fontWeight="medium">
            {line.isShipping ? (
              intl.formatMessage(messages.shippingLineLabel)
            ) : members ? (
              intl.formatMessage(messages.mergedLinesTitle, {
                count: members.length,
                quantity: members.reduce((sum, member) => sum + member.quantity, 0),
              })
            ) : (
              <>
                {line.quantity}× {line.label}
              </>
            )}
          </Text>
        </Box>
        {members ? (
          <>
            {members.map(member => (
              <Text size={2} color="default2" key={member.orderLineId}>
                {intl.formatMessage(messages.mergedMember, {
                  quantity: member.quantity,
                  name: member.label,
                  unitPrice: member.unitPrice.toFixed(2),
                })}
              </Text>
            ))}
            {invoiceLineCount(members) > 1 && (
              <Text size={2} color="warning1">
                {intl.formatMessage(messages.mergedSplitByPrice, {
                  count: invoiceLineCount(members),
                })}
              </Text>
            )}
            <Box>
              <Button
                variant="secondary"
                size="small"
                onClick={onUnmerge}
                data-test-id="cegid-unmerge"
              >
                {intl.formatMessage(messages.unmerge)}
              </Button>
            </Box>
          </>
        ) : (
          <Text size={2} color="default2">
            {intl.formatMessage(messages.linePrice, {
              unitPrice: line.unitPrice.toFixed(2),
              tax: formatPercentage(line.taxPercentage),
            })}
          </Text>
        )}
        {!members && line.discountPercentage > 0 && (
          <Text size={2} color="accent1">
            {intl.formatMessage(messages.discountBadge, {
              percentage: line.discountPercentage,
            })}
          </Text>
        )}
        {line.isShipping && (
          <Text size={2} color="default2">
            {intl.formatMessage(messages.shippingBilledAtOrderCost, {
              price: line.unitPrice.toFixed(2),
            })}
          </Text>
        )}
        {!line.isShipping &&
          !members &&
          line.cegidSalesPrice !== null &&
          line.cegidSalesPrice !== line.unitPrice && (
            <Text size={2} color="default2">
              {intl.formatMessage(messages.cegidCatalogPrice, {
                price: line.cegidSalesPrice.toFixed(2),
              })}
            </Text>
          )}
        {line.existsInCegid ? (
          <Text size={2} color="success1">
            {intl.formatMessage(messages.existsInCegid)}
          </Text>
        ) : (
          <Text size={2} color={line.isShipping ? "critical1" : "warning1"}>
            {intl.formatMessage(
              line.isShipping ? messages.shippingNotInCegid : messages.notInCegid,
            )}
          </Text>
        )}
      </Box>

      {line.isShipping ? (
        <Text size={2} color="default2">
          {line.itemCode}
        </Text>
      ) : (
        <Combobox
          allowCustomValues
          name={`cegid-item-code-${line.orderLineId}`}
          label={intl.formatMessage(messages.itemCodeLabel)}
          options={searchOptions}
          loading={searchResult.loading}
          fetchOptions={query => searchProducts({ variables: { search: query } })}
          value={codeValue}
          onChange={event => {
            const nextCode = (event.target.value as string) ?? "";
            const matched = searchProductsList.find(product => product.itemCode === nextCode);

            onChange({
              itemCode: nextCode,
              itemDescription: matched ? matched.itemDescription : line.itemDescription,
              existsInCegid: !!matched,
              createIfMissing: false,
            });
          }}
        />
      )}

      {line.isShipping ? (
        <Box display="grid" gap={2}>
          <Text size={2} color="default2">
            {intl.formatMessage(messages.shippingUsesCegidName)}
          </Text>
          {extraTextInput}
        </Box>
      ) : (
        <Box display="grid" gap={2}>
          <Input
            name={`cegid-item-description-${line.orderLineId}`}
            label={intl.formatMessage(messages.itemDescriptionLabel)}
            value={line.itemDescription}
            onChange={event => onChange({ itemDescription: event.target.value })}
            helperText={intl.formatMessage(
              line.applyToWholeProduct
                ? messages.referenceRememberedForProduct
                : messages.referenceRememberedForVariant,
            )}
          />
          <Checkbox
            checked={line.applyToWholeProduct}
            onCheckedChange={checked => onChange({ applyToWholeProduct: !!checked })}
          >
            <Text size={2}>{intl.formatMessage(messages.applyToWholeProduct)}</Text>
          </Checkbox>
          {extraTextInput}
          {!line.existsInCegid && (
            <Checkbox
              checked={line.createIfMissing}
              onCheckedChange={checked => onChange({ createIfMissing: !!checked })}
              data-test-id="cegid-create-if-missing"
            >
              <Text size={2}>{intl.formatMessage(messages.createInCegid)}</Text>
            </Checkbox>
          )}
        </Box>
      )}
    </Box>
  );
};

CegidInvoiceLineRow.displayName = "CegidInvoiceLineRow";
export default CegidInvoiceLineRow;
